import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../framework/customStore";
import {
    GeofenceDto,
    GeofenceShapeTypeName,
    SaveGeofenceDto,
} from "../models/geofence/geofence";
import * as geofenceService from "../services/geofenceService";
import { isInstanceOfApiError } from "../services/apiClient";

export interface GeofenceState {
    geofences: GeofenceDto[];
    loading: boolean;
    saving: boolean;
    error: string | null;
    /** Master toggle: edit/draw mode active */
    editMode: boolean;
    /** Which shape to draw (null = select/edit existing) */
    drawingMode: GeofenceShapeTypeName | null;
    showOnMap: boolean;
    filterCustomerIds: string[];
    filterCostCenterIds: string[];
    selectedGeofenceId: string | null;
    stopDurationThresholdMinutes: number;
}

const initialState: GeofenceState = {
    geofences: [],
    loading: false,
    saving: false,
    error: null,
    editMode: false,
    drawingMode: null,
    showOnMap: false,
    filterCustomerIds: [],
    filterCostCenterIds: [],
    selectedGeofenceId: null,
    stopDurationThresholdMinutes: 1.5,
};

export const fetchGeofences = createAsyncThunk(
    "geofence/fetchGeofences",
    async (_params: Record<string, never> = {}, { rejectWithValue }) => {
        try {
            const res = await geofenceService.getGeofences();
            return res.data;
        } catch {
            return rejectWithValue("monitorView.geofence.errorFetch");
        }
    }
);

export const saveGeofenceThunk = createAsyncThunk(
    "geofence/save",
    async (dto: SaveGeofenceDto, { rejectWithValue }) => {
        try {
            const res = dto.geofenceId
                ? await geofenceService.updateGeofence(dto.geofenceId, dto)
                : await geofenceService.createGeofence(dto);
            return res.data;
        } catch (err) {
            if (isInstanceOfApiError(err) && err.status === 409) {
                return rejectWithValue("monitorView.geofence.errorConcurrency");
            }
            return rejectWithValue("monitorView.geofence.errorSave");
        }
    }
);

export const deleteGeofenceThunk = createAsyncThunk(
    "geofence/delete",
    async (geofenceId: string, { rejectWithValue }) => {
        try {
            await geofenceService.deleteGeofence(geofenceId);
            return geofenceId;
        } catch {
            return rejectWithValue("monitorView.geofence.errorDelete");
        }
    }
);

const geofenceSlice = createSlice({
    name: "geofence",
    initialState,
    reducers: {
        setEditMode(state, action: PayloadAction<boolean>) {
            state.editMode = action.payload;
            if (!action.payload) {
                // Turning off edit mode clears drawing and selection
                state.drawingMode = null;
                state.selectedGeofenceId = null;
            } else {
                // Auto-show geofences when entering edit mode
                state.showOnMap = true;
            }
        },
        setDrawingMode(
            state,
            action: PayloadAction<GeofenceShapeTypeName | null>
        ) {
            state.drawingMode = action.payload;
            state.selectedGeofenceId = null;
        },
        setShowOnMap(state, action: PayloadAction<boolean>) {
            state.showOnMap = action.payload;
            if (!action.payload) {
                // Turning off map display also exits edit mode
                state.editMode = false;
                state.drawingMode = null;
                state.selectedGeofenceId = null;
            }
        },
        setFilterCustomerIds(state, action: PayloadAction<string[]>) {
            state.filterCustomerIds = action.payload;
        },
        setFilterCostCenterIds(state, action: PayloadAction<string[]>) {
            state.filterCostCenterIds = action.payload;
        },
        setSelectedGeofenceId(state, action: PayloadAction<string | null>) {
            state.selectedGeofenceId = action.payload;
        },
        setStopDurationThresholdMinutes(state, action: PayloadAction<number>) {
            state.stopDurationThresholdMinutes = action.payload;
        },
        clearError(state) {
            state.error = null;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchGeofences.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchGeofences.fulfilled, (state, action) => {
                state.loading = false;
                state.geofences = action.payload;
            })
            .addCase(fetchGeofences.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(saveGeofenceThunk.pending, (state) => {
                state.saving = true;
                state.error = null;
            })
            .addCase(saveGeofenceThunk.fulfilled, (state, action) => {
                state.saving = false;
                const saved = action.payload;
                const idx = state.geofences.findIndex(
                    (g) => g.geofenceId === saved.geofenceId
                );
                if (idx >= 0) {
                    state.geofences[idx] = saved;
                } else {
                    state.geofences.push(saved);
                }
                state.drawingMode = null;
                state.selectedGeofenceId = null;
            })
            .addCase(saveGeofenceThunk.rejected, (state, action) => {
                state.saving = false;
                state.error = action.payload as string;
            })
            .addCase(deleteGeofenceThunk.pending, (state) => {
                state.error = null;
            })
            .addCase(deleteGeofenceThunk.fulfilled, (state, action) => {
                state.geofences = state.geofences.filter(
                    (g) => g.geofenceId !== action.payload
                );
                state.selectedGeofenceId = null;
            })
            .addCase(deleteGeofenceThunk.rejected, (state, action) => {
                state.error = action.payload as string;
            });
    },
});

export const {
    setEditMode,
    setDrawingMode,
    setShowOnMap,
    setFilterCustomerIds,
    setFilterCostCenterIds,
    setSelectedGeofenceId,
    setStopDurationThresholdMinutes,
    clearError,
} = geofenceSlice.actions;

export const selectVisibleGeofences = createSelector(
    (state: RootState) => state.geofence.geofences,
    (state: RootState) => state.geofence.filterCustomerIds,
    (state: RootState) => state.geofence.filterCostCenterIds,
    (geofences, filterCustomerIds, filterCostCenterIds) =>
        geofences.filter((g) => {
            if (filterCustomerIds.length) {
                const cid = g.customerId?.toLowerCase();
                if (!cid || !filterCustomerIds.some((id) => id.toLowerCase() === cid))
                    return false;
            }
            if (filterCostCenterIds.length) {
                const ccid = g.costCenterId?.toLowerCase();
                if (!ccid || !filterCostCenterIds.some((id) => id.toLowerCase() === ccid))
                    return false;
            }
            return true;
        })
);

export { geofenceSlice };
export const geofenceReducer = geofenceSlice.reducer;
